import { useState, useRef, useEffect, useCallback } from "react";
import type { CommandBarState } from "../hooks/useCommands.ts";
import { searchCommands } from "../editor/commands/index.ts";

interface CommandBarProps {
  barState: CommandBarState;
  isActive: boolean;
  currentPrompt: string;
  onSubmit: (input: string) => void;
  onCancel: () => void;
}

export function CommandBar({
  barState,
  isActive,
  currentPrompt,
  onSubmit,
  onCancel,
}: CommandBarProps) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when active
  useEffect(() => {
    if (isActive && inputRef.current) {
      setInput("");
      inputRef.current.focus();
    }
  }, [isActive, barState]);

  // Update autocomplete suggestions
  useEffect(() => {
    if (barState.mode === "command" && input.length > 0 && !input.startsWith(":")) {
      const matches = searchCommands(input);
      setSuggestions(matches.slice(0, 5).map((c) => c.name));
    } else {
      setSuggestions([]);
    }
  }, [input, barState.mode]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onSubmit(input);
        setInput("");
      } else if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        setInput("");
      } else if (e.key === "Tab") {
        e.preventDefault();
        // Autocomplete
        if (suggestions.length > 0) {
          setInput(suggestions[0]!);
        }
      }
    },
    [input, onSubmit, onCancel, suggestions]
  );

  const placeholder = isActive
    ? barState.mode === "command"
      ? 'Type command or ":viewspec" (Tab to complete, Esc to cancel)'
      : ""
    : 'Press "/" to enter a command';

  return (
    <div className="command-bar">
      <span className="prompt">{isActive ? currentPrompt : ">"}</span>
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        readOnly={!isActive}
        onFocus={() => {
          if (!isActive) {
            // If clicking the command bar, activate it
          }
        }}
      />
      {suggestions.length > 0 && (
        <span className="autocomplete">{suggestions[0]}</span>
      )}
    </div>
  );
}
